import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Trash2 } from "lucide-react";
import { fetchAllUsers, updateUserById } from "../../store";
import "./UserManagement.css";

const umIconProps = { size: 18, strokeWidth: 1.75 };

const UserManagement = () => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.items);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    fullName: "",
    email: "",
    role: "user",
    status: false,
  });
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  // When editingUser changes, update editForm state
  useEffect(() => {
    if (editingUser) {
      setEditForm({
        fullName:
          editingUser.fullName && editingUser.fullName.trim().length > 0
            ? editingUser.fullName
            : `${editingUser.firstName || ""} ${
                editingUser.lastName || ""
              }`.trim(),
        email: editingUser.email || "",
        role: editingUser.role || "user",
        status: editingUser.status === "Active" ? true : false,
      });
    }
  }, [editingUser]);

  // Update filtered users whenever users, filters, or search changes
  useEffect(() => {
    const result = (users || []).filter((u) => {
      const roleMatch = !roleFilter || (u.role || "user") === roleFilter;
      const statusValue = typeof u.status === "string" ? u.status : "";
      const statusMatch =
        !statusFilter ||
        statusValue.toLowerCase() === statusFilter.toLowerCase();
      const searchString =
        (u.fullName || "") +
        " " +
        (u.firstName || "") +
        " " +
        (u.lastName || "") +
        " " +
        (u.email || "");
      const searchMatch =
        !searchFilter ||
        searchString.toLowerCase().includes(searchFilter.toLowerCase());
      return roleMatch && statusMatch && searchMatch;
    });
    setFilteredUsers(result);
  }, [users, roleFilter, statusFilter, searchFilter]);

  const closeAddModal = () => setShowAddModal(false);

  const openEditModal = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };
  const closeEditModal = () => {
    setEditingUser(null);
    setShowEditModal(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editingUser) return;
    // Only fire if the form is different from the original user data
    const isChanged =
      (editingUser.fullName ||
        `${editingUser.firstName || ""} ${
          editingUser.lastName || ""
        }`.trim()) !== editForm.fullName ||
      editingUser.email !== editForm.email ||
      (editingUser.role || "user") !== editForm.role ||
      (editingUser.status.toLowerCase() === "active" ? true : false) !==
        editForm.status;

    if (isChanged) {
      const [firstName, ...rest] = editForm.fullName.split(" ");
      const lastName = rest.join(" ");
      dispatch(
        updateUserById({
          userId: editingUser._id,
          data: {
            ...editingUser,
            fullName: editForm.fullName,
            firstName: firstName,
            lastName: lastName,
            email: editForm.email,
            role: editForm.role,
            isEmailVerified: editForm.isEmailVerified,
            status: editForm.status ? "Active" : "InActive",
          },
        })
      ).then(() => dispatch(fetchAllUsers()));
    }
    closeEditModal();
  };

  return (
    <div>
      {/* Main */}
      <div className="um-main-container">
        <div className="um-page-header">
          <h1 className="um-page-title">User Management</h1>
          <div className="um-page-actions">
            <button className="um-btn-secondary">Export Users</button>
            {/* <button type="button" className="um-btn-primary" onClick={() => setShowAddModal(true)}>
              Add New User
            </button> */}
          </div>
        </div>
        {/* Filters */}
        <div className="um-filters-section">
          <div className="um-filters-row">
            <div className="um-filter-group">
              <label className="um-filter-label">Role</label>
              <select
                className="um-filter-select"
                id="roleFilter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="oper">Operator</option>
                <option value="user">User</option>
              </select>
            </div>
            <div className="um-filter-group">
              <label className="um-filter-label">Status</label>
              <select
                className="um-filter-select"
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="InActive">InActive</option>
                <option value="Banned">Banned</option>
              </select>
            </div>
            <div className="um-filter-group">
              <label className="um-filter-label">Search Users</label>
              <input
                type="text"
                className="um-search-filter"
                id="userSearch"
                placeholder="Search by name or email..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* Users Table */}
        <div className="um-users-table-container">
          <div className="um-table-header">
            <div className="um-table-title">All Users</div>
            <div className="um-table-count">
              Showing <span>{filteredUsers.length}</span> users
            </div>
          </div>

          <table className="um-users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u._id}>
                  <td>
                    <div className="um-user-info">
                      <div className="um-user-avatar">
                        {u.firstName && u.lastName
                          ? `${u.firstName[0]}${u.lastName[0]}`
                          : u.fullName
                          ? u.fullName
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                          : "U"}
                      </div>
                      <div className="um-user-details">
                        <div className="um-user-name">
                          {u.fullName && u.fullName.trim().length > 0
                            ? u.fullName
                            : `${u.firstName || ""} ${u.lastName || ""}`.trim()}
                        </div>
                        <div className="um-user-email">{u.email}</div>
                        <div className="um-user-phone">{u.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`um-role-badge um-role-${u.role || "user"}`}
                    >
                      {u.role || "user"}
                    </span>
                  </td>
                  <td>
                    <span className={`um-status-badge um-status-${u.status}`}>
                      {u.status || "unknown"}
                    </span>
                  </td>
                  <td>
                    {u.createdAt
                      ? new Date(u.createdAt).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    {u.updatedAt
                      ? new Date(u.updatedAt).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <div className="um-action-buttons">
                      <button
                        type="button"
                        className="um-btn-icon um-btn-edit"
                        onClick={() => openEditModal(u)}
                        aria-label="Edit user"
                      >
                        <Pencil {...umIconProps} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="um-btn-icon um-btn-delete"
                        disabled
                        aria-label="Delete user"
                      >
                        <Trash2 {...umIconProps} aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal (integrated from HTML) */}
      {showEditModal && editingUser && (
        <div
          className="um-modal-overlay active"
          onClick={(e) => {
            if (e.target.classList.contains("modal-overlay")) closeEditModal();
          }}
        >
          <div className="um-modal">
            <div className="um-modal-header">
              <h2 className="um-modal-title">Edit User</h2>
              <p className="um-modal-subtitle">
                Modify user details, role, and account status
              </p>
            </div>
            <form id="editUserForm" onSubmit={handleEditSubmit}>
              <div className="um-form-group">
                <label className="um-form-label">Full Name</label>
                <input
                  type="text"
                  className="um-form-input"
                  name="fullName"
                  value={editForm.fullName}
                  onChange={handleEditInputChange}
                  required
                />
              </div>
              <div className="um-form-group">
                <label className="um-form-label">Email Address</label>
                <input
                  type="email"
                  className="um-form-input"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditInputChange}
                  required
                />
              </div>
              <div className="um-form-group">
                <label className="um-form-label">User Role</label>
                <select
                  className="um-form-select"
                  name="role"
                  value={editForm.role}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="user">User</option>
                  <option value="oper">Operator</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="um-form-group">
                <label className="um-form-label">Account Status</label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 8,
                  }}
                >
                  <label className="um-toggle-switch">
                    <input
                      type="checkbox"
                      name="status"
                      checked={editForm.status}
                      onChange={handleEditInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>

                  <span id="statusLabel">
                    {editForm.status ? "Active" : "InActive"}
                  </span>
                </div>
              </div>
              <div className="um-modal-actions">
                <button
                  type="button"
                  className="um-btn-cancel"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button type="submit" className="um-btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="um-modal-overlay um-active">
          <div className="um-modal">
            <div className="um-modal-header">
              <h2 className="um-modal-title">Add New User</h2>
              <p className="um-modal-subtitle">
                Create a new user account with role and status
              </p>
            </div>
            <form>
              <div className="um-form-group">
                <label className="um-form-label">Full Name</label>
                <input type="text" className="um-form-input" required />
              </div>
              <div className="um-form-group">
                <label className="um-form-label">Email</label>
                <input type="email" className="um-form-input" required />
              </div>
              <div className="um-modal-actions">
                <button
                  type="button"
                  className="um-btn-cancel"
                  onClick={closeAddModal}
                >
                  Cancel
                </button>
                <button type="submit" className="um-btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
